/**
 * /ai — Dashboard: headline stat cards (field accuracy, corpus counts,
 * corrections, last canary), document status breakdown, recent runs.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  HStack,
  VStack,
  Text,
  Button,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import { callDocai } from '../../hooks/useDocAI';
import StatusChip from '../../components/AI/StatusChip';

const fmtPct = (x) => (x == null ? '—' : `${(x * 100).toFixed(2)}%`);
const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

export default function AIDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/$/, '');

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await callDocai('docaiStats', { action: 'dashboard' });
      setData(res);
    } catch (err) {
      setError(err.message);
      toast({ title: 'Dashboard load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <Center h="60vh">
        <Spinner size="xl" color="blue.400" />
      </Center>
    );
  }
  if (error && !data) {
    return (
      <Center h="60vh" flexDirection="column">
        <Text color="red.400" mb={3}>{error}</Text>
        <Button leftIcon={<FiRefreshCw />} onClick={load}>Retry</Button>
      </Center>
    );
  }

  const sb = data?.scoreboard || {};
  const evalRuns = sb.evalRuns || [];
  const lastEval = evalRuns[evalRuns.length - 1] || null;
  const canaries = sb.canaries || [];
  const lastCanary = canaries[canaries.length - 1] || null;
  const docs = data?.documents || { total: 0, byStatus: {} };

  const card = (label, value, help) => (
    <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" px={4} py={3}>
      <StatLabel fontSize="xs">{label}</StatLabel>
      <StatNumber fontSize="2xl">{value}</StatNumber>
      {help && <StatHelpText fontSize="xs" mb={0}>{help}</StatHelpText>}
    </Stat>
  );

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={5}>
        <Heading size="md">DocAI Dashboard</Heading>
        <Button size="sm" leftIcon={<FiRefreshCw />} onClick={load} isLoading={loading}>
          Refresh
        </Button>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3} mb={6}>
        {card('Field accuracy (last eval)', lastEval ? fmtPct(lastEval.fieldAccuracy) : '—', lastEval ? `${lastEval.perfectDocs} perfect · ${fmtDate(lastEval.createdAt)}` : 'no evals yet')}
        {card('Documents', docs.total, `${docs.byStatus.auto_accept || 0} auto-accepted`)}
        {card('Review queue', data?.reviewQueueCount ?? 0, 'review + error + unrouted')}
        {card('Goldens', sb.goldens ?? 0, `${sb.silverCorpus?.records ?? 0} silver`)}
        {card('Open corrections', data?.openCorrections ?? 0, `${sb.corrections?.fieldsFixedByHumans ?? 0} fields fixed by humans`)}
        {card('Last canary', lastCanary ? (lastCanary.alarms ? `${lastCanary.alarms} ALARMS` : 'clean') : '—', lastCanary ? fmtDate(lastCanary.createdAt) : 'never run')}
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Status breakdown */}
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4}>
          <Heading size="sm" mb={3}>Document statuses</Heading>
          <VStack align="stretch" spacing={2}>
            {Object.entries(docs.byStatus).map(([status, count]) => (
              <HStack key={status} justify="space-between">
                <StatusChip status={status} />
                <Text fontSize="sm" fontWeight="medium">{count}</Text>
              </HStack>
            ))}
            {!Object.keys(docs.byStatus).length && (
              <Text fontSize="sm" color="gray.500">No documents yet — upload some on the Documents page.</Text>
            )}
          </VStack>
        </Box>

        {/* Recent runs */}
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4}>
          <HStack justify="space-between" mb={3}>
            <Heading size="sm">Recent runs</Heading>
            <Button as={Link} to={`${sectionUrl}/runs`} size="xs" variant="outline">
              All runs
            </Button>
          </HStack>
          <Table size="sm">
            <Thead>
              <Tr><Th>Kind</Th><Th>Skill</Th><Th>Status</Th><Th>Started</Th></Tr>
            </Thead>
            <Tbody>
              {(data?.recentRuns || []).map((run) => (
                <Tr key={run.id}>
                  <Td>
                    <Link to={`${sectionUrl}/runs/${run.id}`}>
                      <Text fontSize="sm" color="blue.400">{run.kind}</Text>
                    </Link>
                  </Td>
                  <Td fontSize="xs">{run.skill_name || '—'}</Td>
                  <Td><StatusChip status={run.status} /></Td>
                  <Td fontSize="xs">{fmtDate(run.created_at)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </SimpleGrid>
    </Box>
  );
}
